import { AlertTriangle } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  ANALOG_BANDWIDTH_OPTIONS,
  DIGITAL_BANDWIDTH_OPTIONS,
  FREQUENCY_STEP_MHZ,
} from '../../constants/suppressions.js';
import {
  useCreateBroadbandSuppression,
  useCreateSuppression,
  useDeviceWhitelist,
  useIntl,
} from '../../hooks';
import { hzToMhz, mhzToHz } from '../../lib/frequency.js';
import { snapToBandwidthOption } from '../../lib/suppression-bandwidth.js';
import { zodResolver } from '../../lib/zod-resolver.js';
import { SignalType } from '../../types';
import type { BroadbandSuppressionMode } from '../../types/broadband-suppression.js';
import {
  type BroadbandSuppressionFormValues,
  buildBroadbandSuppressionSchema,
  buildSolidSuppressionSchema,
} from '../../validations/broadband-suppression.js';
import {
  buildManualSuppressionSchema,
  type ManualSuppressionFormRow,
} from '../../validations/manual-suppression.js';
import { Button, Input, Tabs, TabsContent, TabsList, TabsTrigger } from '../ui';
import { JumperPanel } from './JumperPanel.js';
import { MultiFrequencyPanel } from './MultiFrequencyPanel.js';
import { SignalTypeSelector } from './SignalTypeSelector.js';

const STEP_STANDING_TIME_OPTIONS = Array.from({ length: 20 }, (_, index) => (index + 1) * 5);

const selectClassName =
  'h-10 w-full rounded-md border border-border bg-background-light px-2 text-sm shadow-sm';

export type SuppressionTab = 'regular' | 'solid' | 'hopping' | 'multi' | 'jumper';

/**
 * A tuple rather than an array so an empty `tabs` is unrepresentable: the tab clamp below falls
 * back to the first allowed tab, and with no allowed tabs that is `undefined`, which puts Radix
 * into uncontrolled mode with no active tab and renders an empty bordered box.
 */
export type NonEmptySuppressionTabs = readonly [SuppressionTab, ...SuppressionTab[]];

/**
 * Declaration order of the tab strip, and the only source of it — the strip below maps this, so
 * reordering the constant reorders the triggers. Deliberately NOT the `tabs` default: the two used
 * to be one constant, and adding `jumper` to a shared one would have silently grown a Jumper tab
 * on the Sunflower dialog, which passes no `tabs` at all.
 *
 * Typed as the non-empty tuple rather than `readonly SuppressionTab[]`: a widened element type
 * lets a new member of `SuppressionTab` be forgotten here, which drops it out of every filter
 * below and leaves `allowedTabs` empty for a caller that asked for that tab alone.
 */
const SUPPRESSION_TAB_ORDER = Object.freeze([
  'regular',
  'solid',
  'hopping',
  'multi',
  'jumper',
] as const) satisfies NonEmptySuppressionTabs;

const SUPPRESSION_TAB_LABEL_IDS: Record<SuppressionTab, string> = {
  regular: 'broadbandSuppressionsDialog.form.tabs.regular',
  solid: 'broadbandSuppressionsDialog.form.tabs.solid',
  hopping: 'broadbandSuppressionsDialog.form.tabs.hopping',
  multi: 'broadbandSuppressionsDialog.form.tabs.multi',
  jumper: 'broadbandSuppressionsDialog.form.tabs.jumper',
};

/**
 * Default for the `tabs` prop: the four suppression tabs. `jumper` is opt-in — a jumper session is
 * not a suppression, and only the kvitka detections section asks for it. Frozen at module level
 * rather than defaulted to an inline literal so the default keeps a stable identity across renders
 * and the memo below is not invalidated on every one.
 */
const DEFAULT_SUPPRESSION_TABS = Object.freeze([
  'regular',
  'solid',
  'hopping',
  'multi',
] as const) satisfies NonEmptySuppressionTabs;

type SuppressionFormProps = {
  deviceId: string;
  defaultSuppressionTime: number | null;
  disabled: boolean;
  minAllowedMhz: number | null;
  maxAllowedMhz: number | null;
  maxSuppressionTime: number | null;
  defaultAnalogBandwidthMhz: number;
  defaultDigitalBandwidthMhz: number;
  /** Required, unlike the optional device settings behind them: a missed call site must be a `tsc` error. */
  defaultAnalogMultiFrequencyBandwidthMhz: number;
  defaultDigitalMultiFrequencyBandwidthMhz: number;
  defaultMultiFrequencySignalType: SignalType;
  defaultBroadbandMode?: BroadbandSuppressionMode;
  /** Tab selected on mount, and followed if it changes before the operator picks a tab. Separate from `defaultBroadbandMode`, which is a device setting. */
  initialTab?: SuppressionTab;
  /**
   * Restricts which tabs render, for callers that expose the form for one purpose only (the
   * detections section, which has no use for the `solid` and `hopping` broadband tabs but does
   * want `jumper`). Rendered in `SUPPRESSION_TAB_ORDER` whatever order it is passed in.
   */
  tabs?: NonEmptySuppressionTabs;
  /**
   * `disabled` for the `jumper` tab alone. Split from `disabled` because that one carries a
   * suppression-specific term — a null `maxSuppressionTime` — and a jumper session has no
   * suppression duration to cap (its bound is `MAX_JUMPER_DURATION_MINUTES`), so sharing the flag
   * would lock the jumper form for a reason that does not apply to it. Defaults to `disabled`.
   */
  jumperDisabled?: boolean;
  onSuppressionCreated?: () => void;
};

type FieldProps = {
  label: string;
  htmlFor?: string;
  error?: string;
  className?: string;
  children: ReactNode;
};

type BroadbandSuppressionFormInput = Omit<
  BroadbandSuppressionFormValues,
  'channelFrequencyStart' | 'channelFrequencyStop' | 'duration'
> & {
  channelFrequencyStart: string;
  channelFrequencyStop: string;
  duration: string;
};

function Field({ label, htmlFor, error, className, children }: FieldProps) {
  return (
    <div className={`flex flex-col gap-1 ${className ?? ''}`}>
      {/* Truncate rather than the `whitespace-nowrap` this used to carry: nowrap on a column narrow
          enough to share one row lets a long label spill over its neighbour's. `title` keeps the
          full wording reachable on hover. Mirrors JumperPanel and MultiFrequencyPanel. */}
      <label
        htmlFor={htmlFor}
        title={label}
        className="block truncate text-xs font-medium text-foreground"
      >
        {label}
      </label>
      {children}
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

function BroadbandPanel({
  deviceId,
  defaultSuppressionTime,
  disabled,
  minAllowedMhz,
  maxAllowedMhz,
  maxSuppressionTime,
  defaultDigitalBandwidthMhz,
  isSolid,
  onCreated,
}: {
  deviceId: string;
  defaultSuppressionTime: number | null;
  disabled: boolean;
  minAllowedMhz: number | null;
  maxAllowedMhz: number | null;
  maxSuppressionTime: number | null;
  defaultDigitalBandwidthMhz: number;
  isSolid: boolean;
  onCreated?: () => void;
}) {
  const intl = useIntl();
  const createBroadbandSuppression = useCreateBroadbandSuppression();

  const defaultValues = useMemo<BroadbandSuppressionFormInput>(
    () => ({
      channelFrequencyStart: '',
      channelFrequencyStop: '',
      bandwidth: defaultDigitalBandwidthMhz,
      step: defaultDigitalBandwidthMhz,
      stepStandingTimeMs: 10,
      duration: defaultSuppressionTime != null ? String(defaultSuppressionTime) : '',
    }),
    [defaultDigitalBandwidthMhz, defaultSuppressionTime],
  );

  const validationSchema = useMemo(
    () =>
      isSolid
        ? buildSolidSuppressionSchema(minAllowedMhz, maxAllowedMhz, maxSuppressionTime)
        : buildBroadbandSuppressionSchema(minAllowedMhz, maxAllowedMhz, maxSuppressionTime),
    [isSolid, maxAllowedMhz, maxSuppressionTime, minAllowedMhz],
  );

  const {
    clearErrors,
    formState: { errors, isDirty, isValid, isSubmitting },
    handleSubmit,
    register,
    reset,
    setError,
    setValue,
    watch,
  } = useForm<BroadbandSuppressionFormInput>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
    defaultValues,
  });

  // BroadbandPanel is only ever mounted for one device at a time — the dialog
  // that hosts it unmounts before it can reopen for another device — so a
  // mount-time ref is enough to scope the latch per device, no device-id
  // tracking required.
  const stepTouchedRef = useRef(false);

  // `keepErrors` for the reason spelled out on the Regular panel's copy of this effect: the
  // `''` frequency defaults here match a cleared field just as its `NaN` one does.
  useEffect(() => {
    if (!isDirty) {
      reset(defaultValues, { keepErrors: true });
    }
  }, [defaultValues, isDirty, reset]);

  const bandwidth = watch('bandwidth');

  useEffect(() => {
    // Solid mode has no bandwidth/step fields — the resolver strips them, so
    // there's nothing to follow.
    if (isSolid || stepTouchedRef.current) return;
    const numericBandwidth = Number(bandwidth);
    if (!Number.isFinite(numericBandwidth)) return;
    // shouldDirty keeps step's own dirty/isDirty contribution accurate once
    // it's set programmatically, matching how a manual edit would register.
    setValue('step', numericBandwidth, { shouldValidate: true, shouldDirty: true });
  }, [bandwidth, isSolid, setValue]);

  const stepField = register('step', { valueAsNumber: true });

  const isFieldDisabled = disabled || isSubmitting || createBroadbandSuppression.isPending;
  const isActionDisabled = isFieldDisabled || !isValid;

  const onSubmit = handleSubmit(async (values) => {
    clearErrors('root');

    try {
      await createBroadbandSuppression.mutateAsync({
        deviceId,
        payload: isSolid
          ? {
              type: SignalType.DIGITAL,
              mode: 'solid',
              channelFrequencyStart: mhzToHz(Number(values.channelFrequencyStart)),
              channelFrequencyStop: mhzToHz(Number(values.channelFrequencyStop)),
              duration: Number(values.duration),
            }
          : {
              type: SignalType.DIGITAL,
              mode: 'hopping',
              channelFrequencyStart: mhzToHz(Number(values.channelFrequencyStart)),
              channelFrequencyStop: mhzToHz(Number(values.channelFrequencyStop)),
              bandwidth: mhzToHz(values.bandwidth),
              step: mhzToHz(values.step),
              stepStandingTimeMs: values.stepStandingTimeMs,
              duration: Number(values.duration),
            },
      });
      stepTouchedRef.current = false;
      reset(defaultValues);
      onCreated?.();
    } catch (error) {
      setError('root', {
        type: 'server',
        message:
          error instanceof Error
            ? error.message
            : intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.genericSubmitError' }),
      });
    }
  });

  return (
    // `flex-1` so the fields divide the row's full width instead of leaving a gap on the right —
    // on the solid tab that is three fields sharing it, on hopping six. `min-w-*` is what
    // `flex-wrap` breaks on, so a narrow viewport spills onto a second line rather than squeezing
    // the number inputs.
    <form
      className="flex w-full flex-wrap items-end gap-2"
      data-testid="broadband-suppression-form"
      onSubmit={onSubmit}
    >
      <Field
        label={intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.frequencyStart' })}
        htmlFor="broadband-frequency-start"
        error={errors.channelFrequencyStart?.message}
        className="min-w-32 flex-1"
      >
        <Input
          id="broadband-frequency-start"
          type="number"
          step={FREQUENCY_STEP_MHZ}
          suffix="MHz"
          data-testid="broadband-frequency-start"
          disabled={isFieldDisabled}
          {...register('channelFrequencyStart')}
          onFocus={(event) => event.target.select()}
          className={errors.channelFrequencyStart ? 'border-destructive' : undefined}
        />
      </Field>

      <Field
        label={intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.frequencyStop' })}
        htmlFor="broadband-frequency-stop"
        error={errors.channelFrequencyStop?.message}
        className="min-w-32 flex-1"
      >
        <Input
          id="broadband-frequency-stop"
          type="number"
          step={FREQUENCY_STEP_MHZ}
          suffix="MHz"
          data-testid="broadband-frequency-stop"
          disabled={isFieldDisabled}
          {...register('channelFrequencyStop')}
          onFocus={(event) => event.target.select()}
          className={errors.channelFrequencyStop ? 'border-destructive' : undefined}
        />
      </Field>

      {!isSolid && (
        <>
          <Field
            label={intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.bandwidth' })}
            htmlFor="broadband-bandwidth"
            error={errors.bandwidth?.message}
            className="min-w-20 flex-1"
          >
            <select
              id="broadband-bandwidth"
              data-testid="broadband-bandwidth"
              disabled={isFieldDisabled}
              className={selectClassName}
              {...register('bandwidth', { valueAsNumber: true })}
            >
              {DIGITAL_BANDWIDTH_OPTIONS.map((value) => (
                <option key={`bandwidth-${value}`} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label={intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.step' })}
            htmlFor="broadband-step"
            error={errors.step?.message}
            className="min-w-20 flex-1"
          >
            <select
              id="broadband-step"
              data-testid="broadband-step"
              disabled={isFieldDisabled}
              className={selectClassName}
              {...stepField}
              onChange={(event) => {
                stepTouchedRef.current = true;
                void stepField.onChange(event);
              }}
            >
              {DIGITAL_BANDWIDTH_OPTIONS.map((value) => (
                <option key={`step-${value}`} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label={intl.formatMessage({
              id: 'broadbandSuppressionsDialog.form.stepStandingTime',
            })}
            htmlFor="broadband-step-standing-time"
            error={errors.stepStandingTimeMs?.message}
            className="min-w-24 flex-1"
          >
            <select
              id="broadband-step-standing-time"
              data-testid="broadband-step-standing-time"
              disabled={isFieldDisabled}
              className={selectClassName}
              {...register('stepStandingTimeMs', { valueAsNumber: true })}
            >
              {STEP_STANDING_TIME_OPTIONS.map((value) => (
                <option key={`step-standing-${value}`} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>
        </>
      )}

      <Field
        label={intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.duration' })}
        htmlFor="broadband-duration"
        error={errors.duration?.message}
        className="min-w-24 flex-1"
      >
        <Input
          id="broadband-duration"
          type="number"
          min={1}
          max={maxSuppressionTime ?? undefined}
          suffix="s"
          data-testid="broadband-duration"
          disabled={isFieldDisabled}
          {...register('duration')}
          onFocus={(event) => event.target.select()}
          className={errors.duration ? 'border-destructive' : undefined}
        />
      </Field>

      <Button
        type="submit"
        variant="destructive"
        size="sm"
        className="shrink-0"
        data-testid="broadband-submit"
        disabled={isActionDisabled}
      >
        {createBroadbandSuppression.isPending
          ? intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.genericCreating' })
          : intl.formatMessage({ id: 'broadbandSuppressionsDialog.actions.suppress' })}
      </Button>

      {/* Basis-full, for the reason spelled out on the Regular panel's copy of this row. */}
      {errors.root && (
        <span className="basis-full text-xs text-destructive">{errors.root.message}</span>
      )}
    </form>
  );
}

function RegularPanel({
  deviceId,
  defaultSuppressionTime,
  disabled,
  minAllowedMhz,
  maxAllowedMhz,
  maxSuppressionTime,
  defaultAnalogBandwidthMhz,
  defaultDigitalBandwidthMhz,
  onCreated,
}: {
  deviceId: string;
  defaultSuppressionTime: number | null;
  disabled: boolean;
  minAllowedMhz: number | null;
  maxAllowedMhz: number | null;
  maxSuppressionTime: number | null;
  defaultAnalogBandwidthMhz: number;
  defaultDigitalBandwidthMhz: number;
  onCreated?: () => void;
}) {
  const intl = useIntl();
  const createSuppression = useCreateSuppression();
  const { data: deviceWhitelist } = useDeviceWhitelist(deviceId);

  // `frequency` deliberately has NO default: the operator always types it, and a pre-filled bound
  // (the device's `minAllowedMhz`) reads as a real choice they have to notice and clear. Empty also
  // starts the form invalid, so submit stays disabled until a frequency is entered — with a seeded
  // value the form was valid on mount and one stray click would suppress at the band edge.
  //
  // `NaN`, not `undefined`, is what "empty" must be here: an emptied `valueAsNumber` input reports
  // `NaN`, and RHF's dirty check compares with SameValueZero, under which `NaN` equals `NaN`. With
  // `undefined` (or `''`) the field never matches its own default, so the form reads dirty forever
  // after the first edit to ANY field and the `!isDirty` re-seed below stops firing. Verified
  // against react-hook-form directly, not assumed.
  const defaultValues = useMemo<ManualSuppressionFormRow>(
    () => ({
      id: 'manual-suppression-form',
      type: SignalType.ANALOG,
      frequency: NaN,
      bandwidth: defaultAnalogBandwidthMhz,
      duration: defaultSuppressionTime != null ? String(defaultSuppressionTime) : '',
    }),
    [defaultAnalogBandwidthMhz, defaultSuppressionTime],
  );

  const validationSchema = useMemo(
    () => buildManualSuppressionSchema(minAllowedMhz, maxAllowedMhz, maxSuppressionTime),
    [maxAllowedMhz, maxSuppressionTime, minAllowedMhz],
  );

  const {
    clearErrors,
    formState: { errors, isDirty, isValid, isSubmitting },
    handleSubmit,
    setValue,
    register,
    reset,
    setError,
    watch,
  } = useForm<ManualSuppressionFormRow>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
    defaultValues,
  });

  useEffect(() => {
    // `keepErrors`: an emptied `valueAsNumber` input reports `NaN`, RHF's dirty check compares
    // leaves with `Object.is`, and `Object.is(NaN, NaN)` is true — so clearing the frequency to
    // retype it matches the `NaN` default and reads undirty, re-arming this effect. A plain
    // `reset` clears `_formState.errors`, taking the server message the operator is reading with
    // it; skipping the reset instead would strand the form on stale defaults when device settings
    // refresh while that message is up.
    if (!isDirty) {
      reset(defaultValues, { keepErrors: true });
    }
  }, [defaultValues, isDirty, reset]);

  const selectedType = watch('type');
  const selectedBandwidth = watch('bandwidth');
  const selectedFrequency = watch('frequency');
  const bandwidthOptions =
    selectedType === SignalType.DIGITAL ? DIGITAL_BANDWIDTH_OPTIONS : ANALOG_BANDWIDTH_OPTIONS;
  const previousTypeRef = useRef(selectedType);

  useEffect(() => {
    const defaultBandwidth =
      selectedType === SignalType.DIGITAL ? defaultDigitalBandwidthMhz : defaultAnalogBandwidthMhz;
    const didTypeChange = previousTypeRef.current !== selectedType;

    previousTypeRef.current = selectedType;

    if (didTypeChange || !bandwidthOptions.includes(Number(selectedBandwidth))) {
      setValue('bandwidth', defaultBandwidth, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    bandwidthOptions,
    defaultAnalogBandwidthMhz,
    defaultDigitalBandwidthMhz,
    selectedBandwidth,
    selectedType,
    setValue,
  ]);

  const signalTypeLabel = intl.formatMessage({
    id: 'broadbandSuppressionsDialog.form.signalType',
  });
  const isFieldDisabled = disabled || isSubmitting || createSuppression.isPending;
  const isActionDisabled = isFieldDisabled || !isValid;

  // Advisory only — suppressing a whitelisted frequency is permitted, so this never gates submit.
  const isFrequencyInWhitelist = useMemo(() => {
    if (!deviceWhitelist || !selectedFrequency) return false;

    return deviceWhitelist.entries.some(
      (entry) =>
        selectedFrequency >= hzToMhz(entry.startFrequency) &&
        selectedFrequency <= hzToMhz(entry.endFrequency),
    );
  }, [deviceWhitelist, selectedFrequency]);

  const onSubmit = handleSubmit(async (values) => {
    clearErrors('root');

    try {
      await createSuppression.mutateAsync({
        deviceId,
        payload: {
          channels: [
            {
              channelFrequency: mhzToHz(values.frequency),
              bandwidth: mhzToHz(Number(values.bandwidth)),
              type: values.type,
              duration: Math.max(1, Math.round(Number.parseFloat(values.duration))),
              isManual: true,
            },
          ],
        },
      });
      reset(defaultValues);
      onCreated?.();
    } catch (error) {
      setError('root', {
        type: 'server',
        message:
          error instanceof Error
            ? error.message
            : intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.genericSubmitError' }),
      });
    }
  });

  return (
    // Same idiom as the other panels: `flex-1` so the fields divide the row's full width, `min-w-*`
    // floors for `flex-wrap` to break on. The signal-type selector is excluded — it is a row of
    // fixed-size icon toggles, not a value input, so stretching it would only pad it with dead
    // space.
    <form
      className="flex w-full flex-wrap items-end gap-2"
      data-testid="manual-suppression-form"
      onSubmit={onSubmit}
    >
      <Field label={signalTypeLabel} className="shrink-0">
        <SignalTypeSelector
          groupLabel={signalTypeLabel}
          name="manual-type"
          value={selectedType}
          disabled={isFieldDisabled}
          onChange={(type) => setValue('type', type, { shouldDirty: true, shouldValidate: true })}
        />
      </Field>

      <Field
        label={intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.manual.frequency' })}
        htmlFor="manual-frequency"
        error={errors.frequency?.message}
        className="min-w-32 flex-1"
      >
        <Input
          id="manual-frequency"
          type="number"
          step={FREQUENCY_STEP_MHZ}
          // A floor, because the schema adds its frequency refinements only when the device
          // reports the matching bound and it is positive (`validations/manual-suppression.ts`),
          // and both settings are optional — so for a device that reports neither,
          // `Number.isFinite` is the whole check and 0 or a negative MHz reaches the backend,
          // which 400s with a message that names no field.
          //
          // The step CONSTANT, never `minAllowedMhz`: `min` is also the step base, and
          // `minAllowedFrequency` is any non-negative Hz value the settings DTO accepts, so a
          // device configured at 400_000_005 Hz would put the grid 5 Hz off and give every
          // 10 Hz-aligned entry a stepMismatch. The device's own lower bound is the schema's
          // to enforce, which reports it in-app rather than as a native bubble.
          min={FREQUENCY_STEP_MHZ}
          max={maxAllowedMhz ?? undefined}
          suffix="MHz"
          data-testid="manual-frequency"
          disabled={isFieldDisabled}
          {...register('frequency', { valueAsNumber: true })}
          onFocus={(event) => event.target.select()}
          className={errors.frequency ? 'border-destructive' : undefined}
        />
        {isFrequencyInWhitelist && !errors.frequency && (
          <span className="text-xs text-destructive" data-testid="manual-whitelist-warning">
            <AlertTriangle className="inline h-4 w-4" />
            {intl.formatMessage({ id: 'suppressDialog.row.whitelistWarning' })}
          </span>
        )}
      </Field>

      <Field
        label={intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.bandwidth' })}
        htmlFor="manual-bandwidth"
        error={errors.bandwidth?.message}
        className="min-w-24 flex-1"
      >
        <select
          id="manual-bandwidth"
          data-testid="manual-bandwidth"
          disabled={isFieldDisabled}
          className={selectClassName}
          {...register('bandwidth', { valueAsNumber: true })}
        >
          {bandwidthOptions.map((value) => (
            <option key={`manual-bandwidth-${value}`} value={value}>
              {value}
            </option>
          ))}
        </select>
      </Field>

      <Field
        label={intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.duration' })}
        htmlFor="manual-duration"
        error={errors.duration?.message}
        className="min-w-24 flex-1"
      >
        <Input
          id="manual-duration"
          type="number"
          min={1}
          max={maxSuppressionTime ?? undefined}
          suffix="s"
          data-testid="manual-duration"
          disabled={isFieldDisabled}
          {...register('duration')}
          onFocus={(event) => event.target.select()}
          className={errors.duration ? 'border-destructive' : undefined}
        />
      </Field>

      <Button
        type="submit"
        variant="destructive"
        size="sm"
        className="shrink-0"
        data-testid="manual-submit"
        disabled={isActionDisabled}
      >
        {createSuppression.isPending
          ? intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.genericCreating' })
          : intl.formatMessage({ id: 'broadbandSuppressionsDialog.actions.suppress' })}
      </Button>

      {/* Basis-full so a server error takes its own line under the row rather than squeezing it.
          Wrapped with the button in a `shrink-0` box it could neither shrink nor wrap, so a
          message like "Device is offline" made the row wider than the section — which scrolls
          horizontally, putting the Suppress button out of view. Mirrors JumperPanel. */}
      {errors.root && (
        <span className="basis-full text-xs text-destructive">{errors.root.message}</span>
      )}
    </form>
  );
}

export function SuppressionForm({
  deviceId,
  defaultSuppressionTime,
  disabled,
  minAllowedMhz,
  maxAllowedMhz,
  maxSuppressionTime,
  defaultAnalogBandwidthMhz: rawDefaultAnalogBandwidthMhz,
  defaultDigitalBandwidthMhz: rawDefaultDigitalBandwidthMhz,
  defaultAnalogMultiFrequencyBandwidthMhz: rawDefaultAnalogMultiFrequencyBandwidthMhz,
  defaultDigitalMultiFrequencyBandwidthMhz: rawDefaultDigitalMultiFrequencyBandwidthMhz,
  defaultMultiFrequencySignalType,
  defaultBroadbandMode,
  initialTab,
  tabs = DEFAULT_SUPPRESSION_TABS,
  jumperDisabled,
  onSuppressionCreated,
}: SuppressionFormProps) {
  const intl = useIntl();
  // Snapped here rather than by each caller: a device may be configured at a width the selector
  // does not offer (7 MHz analog), and passed through raw it renders the `<select>` blank while
  // the form still validates and submits it. Every panel below shares these four props, so one
  // caller remembering to snap and another not is the difference between the two dialogs.
  const defaultAnalogBandwidthMhz = snapToBandwidthOption(
    rawDefaultAnalogBandwidthMhz,
    ANALOG_BANDWIDTH_OPTIONS,
  );
  const defaultDigitalBandwidthMhz = snapToBandwidthOption(
    rawDefaultDigitalBandwidthMhz,
    DIGITAL_BANDWIDTH_OPTIONS,
  );
  const defaultAnalogMultiFrequencyBandwidthMhz = snapToBandwidthOption(
    rawDefaultAnalogMultiFrequencyBandwidthMhz,
    ANALOG_BANDWIDTH_OPTIONS,
  );
  const defaultDigitalMultiFrequencyBandwidthMhz = snapToBandwidthOption(
    rawDefaultDigitalMultiFrequencyBandwidthMhz,
    DIGITAL_BANDWIDTH_OPTIONS,
  );
  // Destructured with a default rather than filtered straight into a variable: `tabs` is a
  // non-empty tuple but `filter` erases that, and `allowedTabs[0]` is the clamp fallback for
  // `activeTab`. An `undefined` there flips Radix to uncontrolled with no active tab, which
  // `tsconfig` cannot catch without `noUncheckedIndexedAccess`. The default fires only when a
  // caller names a tab `SUPPRESSION_TAB_ORDER` does not list.
  const allowedTabs = useMemo<NonEmptySuppressionTabs>(() => {
    const [firstAllowed = tabs[0], ...rest] = SUPPRESSION_TAB_ORDER.filter((candidate) =>
      tabs.includes(candidate),
    );
    return [firstAllowed, ...rest];
  }, [tabs]);
  const preferredTab: SuppressionTab =
    initialTab ?? (defaultBroadbandMode === 'hopping' ? 'hopping' : 'solid');
  // Radix renders nothing for a value with no matching TabsContent, so a resolved default the
  // caller excluded would leave an empty panel under a tab strip that looks fine.
  const clampTab = (candidate: SuppressionTab) =>
    allowedTabs.includes(candidate) ? candidate : allowedTabs[0];
  const [tab, setTab] = useState<SuppressionTab>(() => clampTab(preferredTab));

  // The default can change while the form is mounted (the setting is saved from elsewhere, or
  // arrives after a refetch). Adjusted during render, not in an effect, so no commit shows the old
  // tab. Keyed on the derived tab so `solid` -> `multi`, which maps to `solid` both ways, moves
  // nothing. Only until the operator changes tabs (even back to the default) or types into a panel:
  // after that the form stays put, so a later change (e.g. a focus refetch) cannot unmount their
  // panel.
  const [tabPickedByOperator, setTabPickedByOperator] = useState(false);
  const [prevPreferredTab, setPrevPreferredTab] = useState(preferredTab);
  if (preferredTab !== prevPreferredTab) {
    setPrevPreferredTab(preferredTab);
    if (!tabPickedByOperator) {
      setTab(clampTab(preferredTab));
    }
  }

  // Clamped at render, not only in the effect below: the effect is passive, so between the commit
  // that narrows `tabs` and the one the effect schedules there would be a frame whose `value` has
  // no matching TabsContent — a strip with no active trigger over an empty box.
  const activeTab = allowedTabs.includes(tab) ? tab : allowedTabs[0];

  // The derived value above keeps every commit valid; this writes the clamp back so the stale
  // value cannot resurrect if the tab it named becomes allowed again later.
  useEffect(() => {
    if (!allowedTabs.includes(tab)) {
      setTab(allowedTabs[0]);
    }
  }, [allowedTabs, tab]);

  const handleTabChange = useCallback(
    (value: string) => {
      const nextTab = allowedTabs.find((candidate) => candidate === value);
      if (nextTab) {
        setTab(nextTab);
        setTabPickedByOperator(true);
      }
    },
    [allowedTabs],
  );

  return (
    <div className="rounded-lg border border-border bg-background-light p-4">
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        onInputCapture={() => setTabPickedByOperator(true)}
      >
        <TabsList>
          {allowedTabs.map((allowedTab) => (
            <TabsTrigger
              key={allowedTab}
              value={allowedTab}
              data-testid={`suppression-tab-${allowedTab}`}
            >
              {intl.formatMessage({ id: SUPPRESSION_TAB_LABEL_IDS[allowedTab] })}
            </TabsTrigger>
          ))}
        </TabsList>

        {allowedTabs.includes('regular') && (
          <TabsContent value="regular">
            <RegularPanel
              deviceId={deviceId}
              defaultSuppressionTime={defaultSuppressionTime}
              disabled={disabled}
              minAllowedMhz={minAllowedMhz}
              maxAllowedMhz={maxAllowedMhz}
              maxSuppressionTime={maxSuppressionTime}
              defaultAnalogBandwidthMhz={defaultAnalogBandwidthMhz}
              defaultDigitalBandwidthMhz={defaultDigitalBandwidthMhz}
              onCreated={onSuppressionCreated}
            />
          </TabsContent>
        )}

        {allowedTabs.includes('solid') && (
          <TabsContent value="solid">
            <BroadbandPanel
              deviceId={deviceId}
              defaultSuppressionTime={defaultSuppressionTime}
              disabled={disabled}
              minAllowedMhz={minAllowedMhz}
              maxAllowedMhz={maxAllowedMhz}
              maxSuppressionTime={maxSuppressionTime}
              defaultDigitalBandwidthMhz={defaultDigitalBandwidthMhz}
              isSolid={true}
              onCreated={onSuppressionCreated}
            />
          </TabsContent>
        )}

        {allowedTabs.includes('hopping') && (
          <TabsContent value="hopping">
            <BroadbandPanel
              deviceId={deviceId}
              defaultSuppressionTime={defaultSuppressionTime}
              disabled={disabled}
              minAllowedMhz={minAllowedMhz}
              maxAllowedMhz={maxAllowedMhz}
              maxSuppressionTime={maxSuppressionTime}
              defaultDigitalBandwidthMhz={defaultDigitalBandwidthMhz}
              isSolid={false}
              onCreated={onSuppressionCreated}
            />
          </TabsContent>
        )}

        {allowedTabs.includes('multi') && (
          <TabsContent value="multi">
            <MultiFrequencyPanel
              deviceId={deviceId}
              defaultSuppressionTime={defaultSuppressionTime}
              disabled={disabled}
              minAllowedMhz={minAllowedMhz}
              maxAllowedMhz={maxAllowedMhz}
              maxSuppressionTime={maxSuppressionTime}
              defaultAnalogMultiFrequencyBandwidthMhz={defaultAnalogMultiFrequencyBandwidthMhz}
              defaultDigitalMultiFrequencyBandwidthMhz={defaultDigitalMultiFrequencyBandwidthMhz}
              defaultSignalType={defaultMultiFrequencySignalType}
              onCreated={onSuppressionCreated}
            />
          </TabsContent>
        )}

        {allowedTabs.includes('jumper') && (
          <TabsContent value="jumper">
            <JumperPanel
              deviceId={deviceId}
              minAllowedMhz={minAllowedMhz}
              maxAllowedMhz={maxAllowedMhz}
              disabled={jumperDisabled ?? disabled}
              onCreated={onSuppressionCreated}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
